import React from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  TradeRatioStats,
  formatCompactCurrency,
  formatPercentage,
  formatProfitFactor,
} from '../../common/utils/backtestSummaryStats'

function StatTile({
  label,
  value,
  exact,
  color,
}: {
  label: string
  value: string
  /** Full-precision value shown on hover; the tile may round to fit. */
  exact?: string
  color?: string
}) {
  return (
    <Tooltip title={exact && exact !== value ? `${label}: ${exact}` : ''} placement="top">
      <Box
        sx={{
          minWidth: 0,
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          backgroundColor: 'background.custom3',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          noWrap
          sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          noWrap
          sx={{
            fontSize: 15,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: color ?? 'text.primary',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Tooltip>
  )
}

// Whole numbers from 10% up so two- and three-digit drawdowns fit the narrow tile.
function formatDrawdown(value: number | null): string {
  if (value == null) return formatPercentage(value)
  return formatPercentage(value, Math.abs(value) >= 10 ? 0 : 1)
}

function BacktestStatStrip({ stats }: { stats: TradeRatioStats }) {
  const drawdown =
    stats.maxDrawdownPercentage == null ? null : -Math.abs(stats.maxDrawdownPercentage)

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 1,
      }}
    >
      <StatTile
        label="Net P&L"
        value={formatCompactCurrency(stats.netProfit, true)}
        color={stats.netProfit < 0 ? 'error.main' : 'primary.main'}
      />
      <StatTile label="Win rate" value={formatPercentage(stats.winRate, 0)} />
      <StatTile label="Profit factor" value={formatProfitFactor(stats.profitFactor)} />
      <StatTile
        label="Max DD"
        value={formatDrawdown(drawdown)}
        exact={formatPercentage(drawdown, 2)}
        color="error.light"
      />
    </Box>
  )
}

export default BacktestStatStrip
